/**
 * EmailEditorView Component
 *
 * Email-specific editor for EML/MSG files.
 * Features:
 * - Display email content with HTML rendering
 * - macOS/Windows hybrid email window styling
 * - AI-powered entity detection and suggestions
 * - Text redaction with visual highlighting
 * - Download original/redacted versions
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Paperclip } from 'lucide-react';
import { EmailWindow } from './EmailWindow';
import type { HtmlContent } from '../../lib/api';
import type { EntitySuggestion } from '../../types';
import { ENTITY_TYPE_CONFIG, type EntityType } from '../../types';

interface EmailEditorViewProps {
  documentId?: string;
  htmlContent: HtmlContent;
  /** Suggestions passed from parent (already loaded at case level) */
  suggestions?: EntitySuggestion[];
  /** Selected suggestion index from parent (for navigation from ResultsPanel) */
  selectedSuggestionIndex?: number | null;
  /** Callback when user selects a suggestion */
  onSelectSuggestion?: (index: number | null) => void;
}

export function EmailEditorView({
  htmlContent,
  suggestions: propSuggestions = [],
  selectedSuggestionIndex,
  onSelectSuggestion,
}: EmailEditorViewProps) {
  const [error] = useState<string | null>(null);

  // AI Detection state - controlled by parent
  const [localSelectedSuggestion] = useState<number | null>(null);

  // Use parent's selected suggestion if provided, otherwise use local state
  const selectedSuggestion = selectedSuggestionIndex ?? localSelectedSuggestion;
  void onSelectSuggestion; // Available for future use

  // Use suggestions directly from props (controlled component)
  const suggestions = propSuggestions;

  // Ref for scrolling to highlighted text
  const contentRef = useRef<HTMLDivElement>(null);

  // Scroll to suggestion when selectedSuggestionIndex changes from parent (ResultsPanel)
  useEffect(() => {
    if (selectedSuggestionIndex != null && contentRef.current) {
      const highlightedElement = contentRef.current.querySelector(
        `[data-suggestion-index="${selectedSuggestionIndex}"]`
      );
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [selectedSuggestionIndex]);

  // Helper function to apply entity highlights to any text
  const applyHighlights = useCallback((text: string): string => {
    if (!text || suggestions.length === 0) return text;

    let content = text;

    // Sort suggestions by text length (longest first) to avoid partial replacements
    const sortedSuggestions = [...suggestions].sort((a, b) => b.text.length - a.text.length);

    for (const suggestion of sortedSuggestions) {
      const config = ENTITY_TYPE_CONFIG[suggestion.entityType as EntityType] || ENTITY_TYPE_CONFIG.CUSTOM;

      // Determine the display style based on status
      let className = 'email-entity-highlight';
      let displayText = suggestion.text;

      if (suggestion.status === 'approved') {
        className = 'email-entity-redacted';
        displayText = suggestion.replacementText || '[REDACTED]';
      } else if (suggestion.status === 'rejected') {
        className = 'email-entity-rejected';
      }

      // Escape special regex characters in the text
      const escapedText = suggestion.text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

      // Create highlight wrapper with data attributes for navigation
      const highlightHtml = `<span
        class="${className}"
        data-suggestion-index="${suggestion.index}"
        data-entity-type="${suggestion.entityType}"
        style="background-color: ${suggestion.status === 'approved' ? '#1f2937' : config.color}20;
               border-bottom: 2px solid ${config.color};
               padding: 0 2px;
               border-radius: 2px;
               cursor: pointer;
               ${suggestion.status === 'approved' ? 'color: #fff; background-color: #1f2937;' : ''}
               ${selectedSuggestion === suggestion.index ? 'box-shadow: 0 0 0 2px ' + config.color + ';' : ''}"
      >${displayText}</span>`;

      // Replace text occurrences (case-sensitive)
      // Allow span-inside-span since it's just for UI highlighting
      const regex = new RegExp(escapedText, 'g');
      content = content.replace(regex, highlightHtml);
    }

    return content;
  }, [suggestions, selectedSuggestion]);

  // Process HTML content to highlight detected entities
  const processedHtmlContent = useMemo(() => {
    if (!htmlContent.html) return '';
    return applyHighlights(htmlContent.html);
  }, [htmlContent.html, applyHighlights]);

  // Get metadata from HtmlContent
  const metadata = (htmlContent.metadata || {}) as {
    from?: string;
    to?: string;
    cc?: string;
    date?: string;
    attachments?: string[];
    file_type?: string;
  };

  // Calculate stats
  const stats = useMemo(() => {
    const pending = suggestions.filter(s => s.status === 'pending').length;
    const approved = suggestions.filter(s => s.status === 'approved').length;
    const rejected = suggestions.filter(s => s.status === 'rejected').length;
    return { pending, approved, rejected, total: suggestions.length };
  }, [suggestions]);

  return (
    <div className="flex flex-col flex-1 bg-background h-full overflow-hidden">
      {/* Toolbar - placeholder for future editor-specific tools */}
      <div className="bg-card border-b border-border px-6 py-3">
        <div className="flex items-center justify-end flex-wrap gap-4">
          {/* Editor-specific tools can be added here */}
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-destructive/10 border-b border-destructive/20 px-6 py-3">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-hidden flex min-h-0">
        {/* Email Content Area */}
        <div className="flex-1 overflow-auto p-8 bg-muted min-h-0">
          <div className="max-w-4xl mx-auto">
            <EmailWindow
              subject={htmlContent.title || 'No Subject'}
              fromHtml={applyHighlights(metadata.from || 'Unknown Sender')}
              toHtml={applyHighlights(metadata.to || '')}
              ccHtml={metadata.cc ? applyHighlights(metadata.cc) : null}
              date={metadata.date || ''}
              attachments={metadata.attachments}
            >
              <div
                ref={contentRef}
                className="email-body-content prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: processedHtmlContent }}
              />
            </EmailWindow>
          </div>
        </div>

      </div>

      {/* Status Bar */}
      <div className="bg-card border-t border-border px-6 py-2 text-xs text-muted-foreground">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span>Email Editor</span>
            {metadata.attachments && metadata.attachments.length > 0 && (
              <>
                <span>•</span>
                <span className="flex items-center gap-1">
                  <Paperclip className="w-3 h-3" />
                  {metadata.attachments.length} attachment{metadata.attachments.length !== 1 ? 's' : ''}
                </span>
              </>
            )}
            {suggestions.length > 0 && (
              <>
                <span>•</span>
                <span className="text-primary">
                  {stats.pending} AI suggestions pending
                </span>
              </>
            )}
          </div>
          <div className="flex items-center gap-4">
            {stats.approved > 0 && (
              <span className="text-green-600">
                {stats.approved} redaction{stats.approved !== 1 ? 's' : ''} approved
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Styles for email content highlighting */}
      <style>{`
        .email-body-content {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
          line-height: 1.6;
          color: #1f2937;
        }

        .email-body-content img {
          max-width: 100%;
          height: auto;
        }

        .email-entity-highlight {
          transition: all 0.2s ease;
        }

        .email-entity-highlight:hover {
          filter: brightness(0.95);
        }

        .email-entity-redacted {
          font-family: monospace;
          letter-spacing: 0.05em;
        }

        .email-entity-rejected {
          opacity: 0.5;
          text-decoration: line-through;
        }

        @media print {
          .email-entity-highlight,
          .email-entity-redacted {
            background-color: transparent !important;
            border: none !important;
            box-shadow: none !important;
          }

          .email-entity-redacted {
            background-color: #000 !important;
            color: #000 !important;
          }
        }
      `}</style>
    </div>
  );
}
